import React, { useEffect, useMemo, useRef, useState } from "react";
import { Reveal } from "@/components/util/Reveal";

/**
 * Trusted Brands — two horizontal logo marquees.
 *
 * Feb 2026 performance pass (visible-fix rewrite):
 *   Previous implementation used parallel `new Image()` preloaders
 *   as a proxy for row readiness, then revealed the marquee track
 *   by cross-fading its opacity. Because the DOM <img> elements
 *   inside the marquee track paint pixels only when they themselves
 *   have finished decoding, individual logos still visibly popped in
 *   one at a time within the already-revealed row.
 *
 *   The rewrite:
 *     • Row reserves its final height from first paint.
 *     • A hidden loader element mounts <img> tags for each brand to
 *       drive the browser cache and fire onLoad / onError against
 *       the same URLs the marquee will use.
 *     • The marquee track (and its <img> tags) is NOT mounted at all
 *       until every non-failed brand has fully loaded — or a 3 s
 *       safety timeout fires. When it mounts, all images render
 *       from cache immediately as a single visual group and the
 *       animation starts.
 *     • Failed brands are dropped from the render pool; a single
 *       broken CMS URL can never stall or fragment the row.
 *
 *   No per-logo opacity transition, no stagger, no alt/broken-image
 *   surface.
 */

const MIN_BASE = 8;
// Reveal threshold — the marquee reveals as soon as EITHER of these
// two conditions is met (whichever comes first):
//
//   1. `MIN_READY_LOGOS` logos in the initial visible set have been
//      fetched AND decoded — enough to fill the leftmost visible
//      slice of the row so nothing looks empty behind the mask
//      fade. Remaining logos continue loading in the background and
//      appear as the marquee scrolls them into view.
//
//   2. `READY_TIMEOUT_MS` fires — safety net for very slow logo
//      responses. When the mount happens, decoded logos paint from
//      cache; still-decoding logos hide the specific slot via
//      onError until they finish naturally.
//
// This replaces the previous "wait for the full visible set (9
// logos on desktop, 5 on mobile)" gate that could block the row
// for 3+ seconds under /api/uploads concurrency contention.
const MIN_READY_LOGOS = 3;
// Slot width includes the intrinsic logo max-width and the gap
// between slots. Mask fade at row edges hides ~16 % of the width
// so we scale the container by 0.84 before dividing.
const SLOT_WIDTH_MOBILE_PX = 122 + 40;   // max-w-[122px] + gap-10
const SLOT_WIDTH_DESKTOP_PX = 160 + 80;  // sm:max-w-[160px] + lg:gap-24
const VISIBLE_FRACTION = 0.84;           // 1 - 2×mask-fade-width
const SAFETY_BUFFER_LOGOS = 2;
const READY_TIMEOUT_MS = 1200;
// Mobile marquee speed: ~18% slower than the previous slowest row (27.7 px/s).
const COMPACT_SPEED_PX_S = 22.7;

// Compute how many unique brand slots the initial viewport will
// show for this row. Called once per row on mount so mobile and
// desktop each measure themselves.
const computeInitialSetSize = (brandsLength) => {
  if (typeof window === "undefined") return Math.min(4, brandsLength);
  const slotW = window.innerWidth < 640
    ? SLOT_WIDTH_MOBILE_PX
    : SLOT_WIDTH_DESKTOP_PX;
  const visibleW = window.innerWidth * VISIBLE_FRACTION;
  const fits = Math.ceil(visibleW / slotW);
  return Math.min(brandsLength, fits + SAFETY_BUFFER_LOGOS);
};

const buildLoop = (brands) => {
  if (!brands.length) return [];
  const repeats = Math.max(1, Math.ceil(MIN_BASE / brands.length));
  const base = Array.from({ length: repeats }).flatMap(() => brands);
  return [...base, ...base];
};

const MarqueeRow = ({
  brands,
  direction, // "rtl" | "ltr"
  testId,
  offsetPct = 0,
  compact = false, // mobile four-row variant: ~10% larger, aspect-normalised logos
}) => {
  const [loadedIds, setLoadedIds] = useState(() => new Set());
  const [failedIds, setFailedIds] = useState(() => new Set());
  // Ready starts FALSE unconditionally. The preload effect below
  // flips it true once the initial VISIBLE SET has been fetched AND
  // decoded (using HTMLImageElement.decode()) — decode() resolves
  // only when the browser has the pixels ready to paint, so the
  // visible marquee <img> tags never need a second decode tick when
  // they mount. This is what prevents the "logos appear one-by-one"
  // behaviour: previously the readiness gate used onLoad, which
  // fires before decode, so the mounted marquee still had to decode
  // each image separately.
  const [ready, setReady] = useState(false);
  const timeoutFiredRef = useRef(false);

  // Compute the initial visible set size once — the first N unique
  // brands that will occupy the initial viewport slice. Remaining
  // brands continue loading behind the mask fade while the marquee
  // animates them into view.
  const initialSetSize = useMemo(
    () => computeInitialSetSize(brands.length),
    [brands.length],
  );

  // Stable identity for the brand set. When the parent updates the
  // brands array (initial empty → populated fetch response, or a
  // CMS reorder), reset load-tracking + ready so the marquee never
  // mounts with unloaded <img> tags.
  const brandsKey = useMemo(
    () => brands.map((b) => b.id).join(","),
    [brands],
  );
  const prevBrandsKeyRef = useRef("");
  useEffect(() => {
    if (prevBrandsKeyRef.current === brandsKey) return;
    prevBrandsKeyRef.current = brandsKey;
    setLoadedIds(new Set());
    setFailedIds(new Set());
    setReady(false);
    timeoutFiredRef.current = false;
  }, [brandsKey]);

  // Preload + DECODE the initial visible set. `img.decode()` is
  // the standard promise-based API that resolves only when the
  // browser has fetched AND decoded the image pixels — the moment
  // it's actually paintable. Feb 2026: this replaced the earlier
  // onLoad-based readiness which flipped `ready=true` while the
  // browser still had to decode each <img> in the DOM, producing
  // visible one-by-one pop-in on cold loads.
  useEffect(() => {
    if (!brands.length) { setReady(true); return; }
    let cancelled = false;
    const visibleSet = brands.slice(0, initialSetSize);
    // Fire off decode() for every logo in the initial set in
    // parallel. Track completions on the loaded/failed sets so
    // rendering logic can filter failed brands from the marquee.
    visibleSet.forEach((b) => {
      if (!b.logo) {
        if (!cancelled) setLoadedIds((s) => (s.has(b.id) ? s : new Set(s).add(b.id)));
        return;
      }
      const img = new Image();
      img.src = b.logo;
      // decode() throws on network / decode failure. Fall back to
      // onload/onerror for browsers without decode() (very old).
      const p = typeof img.decode === "function"
        ? img.decode()
        : new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
      p.then(() => { if (!cancelled) setLoadedIds((s) => (s.has(b.id) ? s : new Set(s).add(b.id))); })
       .catch(() => { if (!cancelled) setFailedIds((s) => (s.has(b.id) ? s : new Set(s).add(b.id))); });
    });
    // Also pre-fetch the remainder in the background (no gate on
    // them). Once the initial visible set is decoded the marquee
    // reveals; by the time the animation scrolls to the remainder
    // the browser will typically have their bytes in memory-cache.
    brands.slice(initialSetSize).forEach((b) => {
      if (!b.logo) return;
      const img = new Image();
      img.src = b.logo;
      // No await — decode is opportunistic here.
      if (typeof img.decode === "function") img.decode().catch(() => {});
    });
    return () => { cancelled = true; };
  }, [brandsKey, initialSetSize]);

  // Flip ready when EITHER (a) MIN_READY_LOGOS logos in the initial
  // visible set have settled (decoded or failed) — enough to fill the
  // leftmost visible slice cleanly — OR (b) every logo in the
  // visible set has settled (short visible sets like 3-4 brands will
  // hit this first). Failed brands are dropped from the render pool
  // so the marquee never shows a broken slot; the timeout below is
  // a hard-cap safety net for very slow logo responses.
  useEffect(() => {
    if (!brands.length) return; // handled by preload effect above
    const visibleIds = new Set(brands.slice(0, initialSetSize).map((b) => b.id));
    let settled = 0;
    for (const id of visibleIds) {
      if (loadedIds.has(id) || failedIds.has(id)) settled += 1;
    }
    const enough = Math.min(MIN_READY_LOGOS, visibleIds.size);
    if (settled >= enough || settled >= visibleIds.size) setReady(true);
  }, [loadedIds, failedIds, brands, initialSetSize]);

  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => {
      timeoutFiredRef.current = true;
      setReady(true);
    }, READY_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [ready]);

  const visibleBrands = useMemo(
    () => brands.filter((b) => !failedIds.has(b.id)),
    [brands, failedIds],
  );
  const loop = useMemo(() => buildLoop(visibleBrands), [visibleBrands]);

  // Compact (mobile) rows share ONE pixel speed, so rows with different
  // track widths still move identically. Duration = half-track / speed.
  const trackRef = useRef(null);
  const [durationS, setDurationS] = useState(null);
  useEffect(() => {
    const el = trackRef.current;
    if (!compact || !ready || !el || typeof ResizeObserver === "undefined") return undefined;
    const apply = () => {
      const half = el.scrollWidth / 2;
      if (half > 0) setDurationS(half / COMPACT_SPEED_PX_S);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [compact, ready, loop]);

  if (!brands.length) return null;

  return (
    <div
      data-testid={testId}
      data-ready={ready ? "true" : "false"}
      // Reserve the exact final row height from first paint so the
      // rest of the page never shifts as the marquee mounts.
      className={`relative overflow-hidden ${compact ? "h-[46px]" : "h-[42px] sm:h-16"}`}
      style={{
        maskImage:
          "linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0, #000 8%, #000 92%, transparent 100%)",
      }}
    >
      {/* Visible marquee — mounted ONLY after the initial visible
          set has been fetched AND decoded via HTMLImageElement.decode().
          Preloading happens via `new Image()` in the effect above —
          no hidden DOM preloader is needed. By the time this <img>
          tree mounts, the browser holds decoded pixels for every
          logo in the visible set in memory-cache, so they paint on
          the very first frame after mount — no per-logo pop-in. */}
      {ready && (
        <div
          className={`ur-marquee-track ${direction === "ltr" ? "ur-marquee-ltr" : "ur-marquee-rtl"} flex w-max h-full items-center gap-10 sm:gap-20 lg:gap-24 pointer-events-none select-none`}
          ref={trackRef}
          style={{
            animationDelay: offsetPct ? `-${offsetPct}s` : undefined,
            ...(compact && durationS ? { animationDuration: `${durationS.toFixed(2)}s` } : null),
          }}
        >
          {loop.map((b, idx) => (
            <div
              key={`${b.id}-${idx}`}
              className="shrink-0 flex items-center justify-center h-full px-1.5 sm:px-2"
            >
              {b.logo ? (
                <img
                  src={b.logo}
                  alt=""
                  aria-hidden="true"
                  className={`max-h-full ${compact ? "max-w-[134px]" : "max-w-[122px] sm:max-w-[160px]"} w-auto object-contain opacity-60`}
                  style={{ filter: "brightness(0) invert(1)" }}
                  onLoad={compact ? (e) => {
                    // Height ∝ 1/√aspect so wide and compact marks carry similar visual weight.
                    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                    if (!w || !h) return;
                    const r = w / h;
                    // Vacation Aruba's wordmark reads oversized at this scale — trim ~22%.
                    const k = /vacation\s*aruba/i.test(b.name || "") ? 0.78 : 1;
                    e.currentTarget.style.height = `${Math.min(38, 50 / Math.sqrt(r), 134 / r) * k}px`;
                  } : undefined}
                  draggable={false}
                  // Defence-in-depth: hide the specific slot if a
                  // cached image somehow fails to render (should
                  // never happen because failed brands are filtered
                  // from the render pool, but keeps the marquee
                  // guaranteed clean of alt/broken-image surfaces).
                  onError={(e) => {
                    e.currentTarget.style.visibility = "hidden";
                  }}
                />
              ) : (
                <span className="font-display text-sm sm:text-lg font-bold text-white tracking-tight opacity-60">
                  {b.name}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const TrustedBrands = ({ brands = [], eyebrow }) => {
  const row1 = useMemo(
    () => brands.filter((b) => Number(b.row) !== 2),
    [brands],
  );
  const row2 = useMemo(
    () => brands.filter((b) => Number(b.row) === 2),
    [brands],
  );

  // Mobile: same 16 logos, split into four rows of the existing marquee.
  const mobileRows = useMemo(() => {
    const all = [...row1, ...row2];
    const per = Math.ceil(all.length / 4);
    return [0, 1, 2, 3].map((i) => all.slice(i * per, (i + 1) * per)).filter((r) => r.length);
  }, [row1, row2]);

  if (!brands.length) return null;

  return (
    <section
      data-testid="trusted-brands"
      className="bg-[#0F0F0F] pt-12 pb-6 md:pb-20 sm:pt-[88px] lg:pt-[112px] min-[1440px]:pt-[50px] lg:pb-24 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div
          data-testid="brands-eyebrow"
          className="text-[#A3A3A3] text-[11px] sm:text-xs font-bold tracking-[0.14em] sm:tracking-[0.2em] uppercase text-center leading-relaxed whitespace-nowrap sm:whitespace-normal overflow-hidden text-ellipsis sm:overflow-visible"
        >
          {eyebrow || "Trusted By Leading Brands"}
        </div>
      </div>

      <Reveal data-testid="brands-marquee-mobile" className="md:hidden mt-5 space-y-[18px]">
        {mobileRows.map((rowBrands, i) => (
          <MarqueeRow
            key={i}
            brands={rowBrands}
            direction={i % 2 === 0 ? "rtl" : "ltr"}
            testId={`brands-marquee-mobile-row-${i + 1}`}
            offsetPct={i % 2 === 0 ? 0 : 12.5}
            compact
          />
        ))}
      </Reveal>

      <Reveal data-testid="brands-marquee" className="hidden md:block mt-5 space-y-6">
        <MarqueeRow
          brands={row1}
          direction="rtl"
          testId="brands-marquee-row-1"
        />
        {row2.length > 0 && (
          <MarqueeRow
            brands={row2}
            direction="ltr"
            testId="brands-marquee-row-2"
            offsetPct={12.5}
          />
        )}
      </Reveal>

      <style>{`
        @keyframes ur-marquee-rtl {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes ur-marquee-ltr {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .ur-marquee-track {
          animation-duration: 50s;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
          will-change: transform;
        }
        .ur-marquee-rtl { animation-name: ur-marquee-rtl; }
        .ur-marquee-ltr { animation-name: ur-marquee-ltr; }
        @media (max-width: 639px) {
          .ur-marquee-track { animation-duration: 40s; }
        }
        @media (prefers-reduced-motion: reduce) {
          .ur-marquee-track { animation-play-state: paused !important; }
        }
      `}</style>
    </section>
  );
};
